/**
 * The pandit roster: who we can send, where they are in onboarding, and what they may perform.
 *
 * **The activation gate is the substance of this screen.** A pandit becomes matchable only when
 * every requirement is met, and the checklist here is computed by the server on every read rather
 * than stored — a cached one goes stale the moment a document expires or a skill is un-confirmed.
 * The `activate` button is always offered: the server re-runs the gate when it is pressed, because
 * a requirement can lapse between somebody reading the checklist and acting on it, and a client
 * that decided the button was legal would be the one guessing.
 *
 * **Confirming a skill is not bookkeeping.** `confirmed_at` is the only thing the matching engine
 * reads, so pressing confirm is deciding that this person may be sent to a stranger's home to
 * perform that particular ritual. Unconfirming takes a reason, and the server refuses to withdraw
 * the last confirmation on an active profile — that is a demotion, and doing both in one click
 * would hide which decision was actually made.
 *
 * **Every tab reads a server endpoint.** Rates, ratings, recent reviews and walk-away counts come
 * from the quality read, jobs from the history read, money from the payables and ledger reads.
 * Nothing on the drawer is a fixture or a blank standing in for a missing endpoint.
 */
import { useState } from 'react';
import { BADGE_TYPES, Chip, Icon, Kicker, Note, StatusChip } from '../components/primitives';
import { Input, Select, Toggle } from '../components/forms';
import type { components } from '../api/generated/schema';
import { DataTable, DetailPage } from '../components/data';
import { KV, Timeline } from '../components/entities';
import { jobsTimeline } from './panditJobs';
import { ConfirmAction, FilterBar, ScopeAction, Section, Sparkline, StatTile } from '../components/kit';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { QUEUE_HINT, useQueue } from '../lib/useQueue';
import { count } from '../lib/text';
import { useConsole } from '../app/console';
import { can } from '../lib/rbac';
import { PanditMoney } from './PanditMoney';
import { PanditAvailability } from './PanditAvailability';
import { AuditTrailPanel } from '../components/audit';
import { errorMessage } from '../lib/api/client';
import { PUBLIC_SITE_ORIGIN } from '../lib/canonicalHost';
import { useCities } from '../lib/api/hooks/geo';
import {
  useConfirmAllSkills,
  useConfirmSkill,
  useIssueQualityAction,
  usePandit,
  usePanditJobs,
  usePanditQuality,
  usePanditSkills,
  usePandits,
  useRevokeQualityAction,
  useRevealPanditPhone,
  useSetPanditListing,
  useSetPanditStatus,
  useSetProficiency,
  useUnconfirmSkill,
  type PanditQuality,
  type PanditRow,
  type QualityActionIssue,
} from '../lib/api/hooks/pandits';
import { useCategories } from '../lib/api/hooks/catalogue';
import { useUrlSearch, useUrlTab } from '../lib/filterCodecs';
import { formatDateTime } from '../lib/datetime';

const PAGE_SIZE = 50;

/**
 * The tabs are server-side filters, one per lifecycle status, and so are the search box, city,
 * listing and badge (admin doc 07 §3.1): a client-side `.filter()` over one page would search
 * fifty rows and confidently report no match for a pandit on page two.
 */
const TABS = [
  { key: '', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'submitted', label: 'Submitted' },
  { key: 'in_review', label: 'In review' },
  { key: 'active', label: 'Active' },
  { key: 'suspended', label: 'Suspended' },
  { key: 'deactivated', label: 'Offboarded' },
] as const;

/** The tab rides the URL (doc 05-admin/04 §3.3), so a pasted link opens the same queue. */
const TAB_KEYS = TABS.map((t) => t.key);

const LISTING = { Listed: true, Hidden: false } as const;
const BADGE_BY_LABEL = new Map<string, string>(Object.entries(BADGE_TYPES).map(([type, b]) => [b.label, type]));

const STATUS_TONE: Record<string, 'success' | 'warn' | 'error' | 'neutral'> = {
  active: 'success',
  submitted: 'warn',
  in_review: 'warn',
  suspended: 'error',
  deactivated: 'error',
  draft: 'neutral',
};

/**
 * The lifecycle actions the server accepts, split by whether they need the safety component.
 *
 * `ConfirmAction` makes the operator type a phrase and give a reason. That friction is the feature
 * for the three that hurt — but putting it in front of "start review" would train people to type
 * through it, which is how the friction stops working on the actions that need it.
 */
const PLAIN_ACTIONS = [
  { key: 'start_review', label: 'Start review' },
  { key: 'activate', label: 'Activate' },
  { key: 'unsuspend', label: 'Reinstate' },
] as const;

const GUARDED_ACTIONS = [
  {
    key: 'reject',
    label: 'Reject',
    phrase: 'REJECT',
    tone: 'error' as const,
    consequences: [
      'Sends the profile back to draft with the checklist reopened.',
      'Your reason reaches the pandit verbatim, so it has to be something they can act on.',
    ],
  },
  {
    key: 'suspend',
    label: 'Suspend',
    phrase: 'SUSPEND',
    tone: 'error' as const,
    consequences: [
      'Removes this pandit from every future dispatch wave immediately.',
      'Bookings already confirmed are not cancelled here — ops move those separately.',
      'Sessions are NOT revoked: the same person keeps using the app as a devotee, which is not a punishment anybody chose.',
    ],
  },
  {
    key: 'deactivate',
    label: 'Deactivate',
    phrase: 'DEACTIVATE',
    tone: 'error' as const,
    consequences: [
      'Permanent. A returning pandit is reinstated by support, never re-registered — a second profile would split their bookings, ratings and payouts in two.',
      'Needs superadmin, co-signed by a fresh sign-in: a session signed in more than a few minutes ago is asked to unlock the console with password and code first. The server refuses it from supply_kyc alone.',
    ],
  },
] as const;

/**
 * The lifecycle actions the server's transition table allows from `status`
 * (`pandit_onboarding.ALLOWED_TRANSITIONS`). Offering Activate on an active pandit, or Reinstate
 * on one who was never suspended, invited a click the server either refused or took as a no-op.
 */
const MOVES_FROM: Readonly<Record<string, readonly string[]>> = {
  submitted: ['start_review'],
  in_review: ['activate', 'reject'],
  active: ['start_review', 'suspend', 'deactivate'],
  suspended: ['unsuspend', 'deactivate'],
};

export function lifecycleActions(status: string): readonly string[] {
  return MOVES_FROM[status] ?? [];
}

/**
 * Every checklist item is part of the activation gate, and reinstating re-checks that gate. A
 * suspension over unmet items cannot be undone from here — found live, on a seeded pandit that
 * was active without them. Said before the suspend, not discovered after it.
 */
export function reinstateWarning(checklist: readonly { key: string; done: boolean }[]): string | null {
  const unmet = checklist.filter((item) => !item.done).map((item) => item.key.replace(/_/g, ' '));
  return unmet.length === 0
    ? null
    : `Reinstating is refused until every requirement is met, and these are not: ${unmet.join(', ')}.`;
}

/** Why a status offers no action, so an empty bar never reads as a broken one. */
export function noMovesLine(status: string): string {
  return status === 'deactivated'
    ? 'Offboarded for good. Support un-deactivates a returning pandit; nobody re-applies.'
    : 'Nothing to decide yet: the pandit submits the profile from the app.';
}

const DETAIL_TABS = ['Onboarding', 'Skills', 'Quality', 'Jobs', 'Availability', 'Money', 'Profile', 'History'];

/** The server's own enum values — a label map would let a hand-typed one through. */
const PROFICIENCIES = ['qualified', 'expert'] as const;

/** `PanditStatusReason`: what the pandit's paused screen names, in their language, from
 * `enum_label`. Only a pause carries one; the server refuses it on any other action. */
const PAUSE_REASONS: readonly NonNullable<components['schemas']['StatusActionIn']['reason_code']>[] = [
  'quality_review',
  'document_expired',
  'policy',
  'on_request',
];
const PAUSES: ReadonlySet<string> = new Set(['suspend', 'deactivate']);

export function statusTone(status: string): 'success' | 'warn' | 'error' | 'neutral' {
  return STATUS_TONE[status] ?? 'neutral';
}

/** A machine status is not a word an operator reads. Presentation only — nothing is invented. */
export function statusLabel(status: string): string {
  return status.replace(/_/g, ' ');
}

/**
 * The public profile's path, composed as the site's `repository.Pandit.path` composes it: the code
 * is the identity, the slug is decoration (docs/06-website/11).
 */
export function publicProfilePath(slug: string, publicCode: string): string {
  return `/pandit/${slug}-${publicCode}`;
}

export function PanditDirectory() {
  const { scope, toast } = useConsole();
  const [tab, setTab] = useUrlTab('pandits', TAB_KEYS, '');
  const [detailTab, setDetailTab] = useState('Onboarding');
  const [pending, setPending] = useState<(typeof GUARDED_ACTIONS)[number] | null>(null);
  const [reasonCode, setReasonCode] = useState('');
  const [listingConfirm, setListingConfirm] = useState(false);
  const [phoneConfirm, setPhoneConfirm] = useState(false);
  // Keyed by the pandit, so opening the next drawer never shows the last one's number.
  const [revealedPhone, setRevealedPhone] = useState<{ id: number; phone: string } | null>(null);
  const [unconfirming, setUnconfirming] = useState<number | null>(null);

  const [q, setQ] = useUrlSearch('pandits');
  const [city, setCity] = useState('');
  const [listedFilter, setListedFilter] = useState('');
  const [badge, setBadge] = useState('');
  const cities = useCities({ page: 1, page_size: 100 });
  const cityId = (cities.data?.items ?? []).find((c) => c.name === city)?.id;
  const roster = usePandits({
    page: 1,
    page_size: PAGE_SIZE,
    status: tab || undefined,
    city_id: cityId,
    q: q.trim() || undefined,
    is_listed: listedFilter in LISTING ? LISTING[listedFilter as keyof typeof LISTING] : undefined,
    badge_type: BADGE_BY_LABEL.get(badge),
  });
  const rows: PanditRow[] = roster.data?.items ?? [];
  const { focus, setFocus, open, setOpen } = useQueue(rows, { key: tab });

  const detail = usePandit(open?.id);
  // The row was the status when the drawer opened; the detail is re-read after every action.
  const liveStatus = detail.data?.status ?? open?.status ?? '';
  const moves = lifecycleActions(liveStatus);
  const skills = usePanditSkills(open?.id);
  const quality = usePanditQuality(detailTab === 'Quality' ? open?.id : undefined);
  const jobs = usePanditJobs(detailTab === 'Jobs' ? open?.id : undefined);

  const setStatus = useSetPanditStatus();
  const setListing = useSetPanditListing();
  const revealPhone = useRevealPanditPhone();
  const confirmSkill = useConfirmSkill();
  const unconfirmSkill = useUnconfirmSkill();
  const setProficiency = useSetProficiency();
  const confirmAll = useConfirmAllSkills();

  /**
   * The server's refusal is shown verbatim. Its invariants — the transition table, the activation
   * gate, the last-confirmed-skill rule, the superadmin-only deactivation — are its to enforce and
   * its to explain, and a client that paraphrased one would eventually be wrong and confident.
   */
  const outcome = (message: string, tone: 'success' | 'info' | 'error' = 'success') => ({
    onSuccess: () => toast({ tone, message }),
    onError: (e: unknown) => toast({ tone: 'error' as const, message: errorMessage(e) }),
  });

  const run = (
    id: number,
    action: string,
    label: string,
    reason?: string,
    reason_code?: string,
    // The toast says whether it worked; the dialog's danger tone already said what it meant.
  ) => setStatus.mutate({ id, action, reason, reason_code }, outcome(`${label} — done`));

  const suspendWarning = reinstateWarning(detail.data?.checklist ?? []);
  const actionDialog = pending && open && (
    <ConfirmAction
      open
      title={`${pending.label} ${open.display_name}?`}
      phrase={pending.phrase}
      tone={pending.tone}
      reason
      consequences={
        pending.key === 'suspend' && suspendWarning ? [...pending.consequences, suspendWarning] : pending.consequences
      }
      onCancel={() => {
        setPending(null);
        setReasonCode('');
      }}
      onConfirm={(reason: string) => {
        const action = pending;
        setPending(null);
        setReasonCode('');
        run(open.id, action.key, action.label, reason, reasonCode);
      }}
      confirmLabel={pending.label}
    >
      {PAUSES.has(pending.key) && (
        <Select
          label="What the pandit sees"
          value={reasonCode}
          options={PAUSE_REASONS}
          optionLabel={(code) => code.replace(/_/g, ' ')}
          placeholder="No reason shown"
          onChange={setReasonCode}
        />
      )}
    </ConfirmAction>
  );

  const listingDialog = open && (
    <ConfirmAction
      open={listingConfirm}
      title={detail.data?.is_listed ? 'Hide this profile from discovery?' : 'List this profile again?'}
      phrase={detail.data?.is_listed ? 'HIDE' : 'LIST'}
      reason
      consequences={[
        'Listing is separate from status: hiding a profile does not interrupt bookings already in flight.',
        'The public website reflects it at the next build, not immediately.',
      ]}
      onCancel={() => setListingConfirm(false)}
      onConfirm={(reason: string) => {
        setListingConfirm(false);
        setListing.mutate(
          { id: open.id, is_listed: !detail.data?.is_listed, reason },
          outcome(detail.data?.is_listed ? 'Profile hidden from discovery' : 'Profile listed'),
        );
      }}
      confirmLabel={detail.data?.is_listed ? 'Hide profile' : 'List profile'}
    />
  );

  const phoneDialog = open && (
    <ConfirmAction
      open={phoneConfirm}
      title="Reveal this pandit's phone number?"
      phrase="REVEAL"
      tone="warn"
      reason="Why you need to call them (reaches the audit log)"
      consequences={[
        'Access is logged: your name and reason are written to the audit log before the number is shown.',
        'The log records that the number was read, never the number itself.',
      ]}
      onCancel={() => setPhoneConfirm(false)}
      onConfirm={(reason: string) => {
        setPhoneConfirm(false);
        revealPhone.mutate(
          { id: open.id, reason },
          {
            onSuccess: (r) => {
              setRevealedPhone({ id: open.id, phone: r.phone });
              toast({ tone: 'info', message: 'Phone number revealed · audited' });
            },
            onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
          },
        );
      }}
      confirmLabel="Reveal and record"
    />
  );

  const unconfirmDialog = open && (
    <ConfirmAction
      open={unconfirming !== null}
      title="Withdraw this confirmation?"
      phrase="WITHDRAW"
      tone="error"
      reason
      consequences={[
        'The pandit stops being matchable for this puja immediately.',
        'If it is their only confirmed puja and they are active, the server refuses — that is a demotion, and it is a separate decision.',
      ]}
      onCancel={() => setUnconfirming(null)}
      onConfirm={(reason: string) => {
        const skillId = unconfirming;
        setUnconfirming(null);
        if (skillId !== null) {
          unconfirmSkill.mutate(
            { id: open.id, skillId, reason },
            outcome('Confirmation withdrawn', 'info'),
          );
        }
      }}
      confirmLabel="Withdraw"
    />
  );

  if (open) {
    const d = detail.data;
    const skillRows = skills.data?.items ?? [];
    const unconfirmed = skillRows.filter((s) => s.confirmed_at === null);

    return (
      <DetailPage
        title={open.display_name}
        code={open.public_code}
        status={<StatusChip tone={statusTone(liveStatus)} label={statusLabel(liveStatus)} />}
        tabs={DETAIL_TABS}
        activeTab={detailTab}
        onTab={setDetailTab}
        onClose={() => setOpen(null)}
        actions={
          <div style={{ display: 'flex', gap: 'var(--sp-sm)', flexWrap: 'wrap' }}>
            {moves.length === 0 && <Note>{noMovesLine(liveStatus)}</Note>}
            {PLAIN_ACTIONS.filter((a) => moves.includes(a.key)).map((a) => (
              <ScopeAction
                key={a.key}
                scope={scope}
                need="supply_kyc"
                size="sm"
                onClick={() => run(open.id, a.key, a.label)}
              >
                {a.label}
              </ScopeAction>
            ))}
            {GUARDED_ACTIONS.filter((a) => moves.includes(a.key)).map((a) => (
              <ScopeAction
                key={a.key}
                scope={scope}
                need={a.key === 'deactivate' ? 'superadmin' : 'supply_kyc'}
                size="sm"
                onClick={() => setPending(a)}
              >
                {a.label}
              </ScopeAction>
            ))}
          </div>
        }
      >
        {detailTab === 'Onboarding' && (
          <Section title="Activation checklist">
            <Note>
              Computed on every read, never stored — a cached checklist goes stale the moment a
              document expires.
            </Note>
            {detail.isPending && <LoadingState message="Reading the checklist…" />}
            {detail.isError && <ErrorState title="Could not read this pandit" cause={errorMessage(detail.error)} />}
            {d && (
              <div style={{ display: 'grid', gap: 'var(--sp-xs)', marginTop: 'var(--sp-md)' }}>
                {d.checklist.map((item) => (
                  <div
                    key={item.key}
                    style={{
                      display: 'flex',
                      gap: 'var(--sp-sm)',
                      alignItems: 'flex-start',
                      padding: 'var(--sp-sm) var(--sp-md)',
                      border: '1px solid var(--c-border)',
                      background: item.done ? 'var(--c-surface2)' : 'var(--c-sunk)',
                      borderRadius: 'var(--r-tile)',
                    }}
                  >
                    <Icon name={item.done ? 'check' : 'clock'} />
                    <div>
                      <div style={{ fontWeight: 500 }}>{item.key.replace(/_/g, ' ')}</div>
                      <div style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                        {item.detail}
                      </div>
                    </div>
                    {!item.done && !item.blocking && (
                      <Chip label="ours to do" style={{ marginLeft: 'auto' }} />
                    )}
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {detailTab === 'Skills' && (
          <Section
            title="Pujas this pandit may be sent for"
            actions={
              unconfirmed.length > 0 ? (
                <ScopeAction
                  scope={scope}
                  need="supply_kyc"
                  size="sm"
                  onClick={() =>
                    confirmAll.mutate(
                      { id: open.id, skillIds: unconfirmed.map((s) => s.id) },
                      outcome(`${unconfirmed.length} confirmed`),
                    )
                  }
                >
                  Confirm all {unconfirmed.length}
                </ScopeAction>
              ) : undefined
            }
          >
            <Note>
              Only a confirmed puja is matchable. An unconfirmed row is a claim nobody has checked.
            </Note>
            {skills.isPending && <LoadingState message="Reading declared pujas…" />}
            {skills.isError && <ErrorState title="Could not read skills" cause={errorMessage(skills.error)} />}
            {skills.isSuccess && skillRows.length === 0 && (
              <EmptyState
                icon="list-checks"
                title="No pujas listed yet"
                body="The pandit adds these in the app. Nothing to confirm until they do."
                inline
              />
            )}
            {skillRows.length > 0 && (
              <div style={{ display: 'grid', gap: 'var(--sp-xs)', marginTop: 'var(--sp-md)' }}>
                {skillRows.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      display: 'flex',
                      gap: 'var(--sp-sm)',
                      alignItems: 'center',
                      padding: 'var(--sp-sm) var(--sp-md)',
                      border: '1px solid var(--c-border)',
                      borderRadius: 'var(--r-tile)',
                      background: 'var(--c-surface2)',
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{s.puja.name}</div>
                      <div style={{ color: 'var(--c-ink2)', fontSize: 'var(--fs-admin-colhead)' }}>
                        {s.min_notice_hours} h notice{s.is_primary ? ' · specialisation' : ''}
                      </div>
                    </div>
                    <StatusChip
                      tone={s.confirmed_at ? 'success' : 'warn'}
                      label={s.confirmed_at ? 'confirmed' : 'pending'}
                    />
                    <Chip label={s.proficiency} />
                    {s.confirmed_at ? (
                      <ScopeAction
                        scope={scope}
                        need="supply_kyc"
                        size="sm"
                        onClick={() => setUnconfirming(s.id)}
                      >
                        Withdraw
                      </ScopeAction>
                    ) : (
                      <ScopeAction
                        scope={scope}
                        need="supply_kyc"
                        size="sm"
                        onClick={() =>
                          confirmSkill.mutate({ id: open.id, skillId: s.id }, outcome('Confirmed'))
                        }
                      >
                        Confirm
                      </ScopeAction>
                    )}
                    <Select
                      label="Proficiency"
                      value={s.proficiency}
                      options={PROFICIENCIES}
                      onChange={(v) =>
                        setProficiency.mutate(
                          {
                            id: open.id,
                            skillId: s.id,
                            proficiency: v,
                            reason: 'Set from the pandit directory',
                          },
                          outcome(`Marked ${v}`),
                        )
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {detailTab === 'Quality' && (
          <QualityPanel
            panditId={open.id}
            query={quality}
            onSuspend={() => setPending(GUARDED_ACTIONS.find((a) => a.key === 'suspend')!)}
            scope={scope}
          />
        )}

        {detailTab === 'Jobs' && (
          <Section title="Jobs" note="Assignments and payouts, newest first">
            {jobs.isPending && <LoadingState message="Reading their jobs…" />}
            {jobs.isError && <ErrorState title="Could not read their jobs" cause={errorMessage(jobs.error)} />}
            {jobs.isSuccess &&
              (jobs.data.items.length === 0 ? (
                <EmptyState title="No jobs yet" body="Nothing has been assigned to this pandit." />
              ) : (
                <Timeline events={jobsTimeline(jobs.data.items)} />
              ))}
          </Section>
        )}

        {detailTab === 'Availability' && <PanditAvailability panditId={open.id} />}

        {detailTab === 'History' && <AuditTrailPanel entityType="pandit_profile" entityId={open.id} />}

        {detailTab === 'Money' &&
          (can(scope, 'finance') ? (
            <PanditMoney panditId={open.id} />
          ) : (
            <Section title="Money">
              <EmptyState title="Requires scope: finance" body="What we owe a pandit and what has reached them is read by finance. Ask a finance admin." />
            </Section>
          ))}

        {detailTab === 'Profile' && d && (
          <Section title="Profile">
            <KV
              rows={[
                ['Name in Hindi', d.display_name_hi ?? '—'],
                ['Public code', d.public_code],
                ['Slug', d.slug],
                [
                  'Public page',
                  // Only an active, listed profile is built into the site; the page follows the next build.
                  d.is_listed && d.status === 'active' ? (
                    <a href={`${PUBLIC_SITE_ORIGIN}${publicProfilePath(d.slug, d.public_code)}`} target="_blank" rel="noopener noreferrer">
                      {publicProfilePath(d.slug, d.public_code)}
                    </a>
                  ) : (
                    'not published — only an active, listed profile has a page'
                  ),
                ],
                ['Phone', revealedPhone?.id === open.id ? revealedPhone.phone : d.masked_phone],
                ['Base city', d.base_city_name ?? `#${d.base_city_id}`],
                ['Languages', d.languages.length ? d.languages.join(', ') : 'none listed'],
                ['Sampradaya', d.sampradayas.length ? d.sampradayas.join(', ') : 'none listed'],
                ['Experience', `${d.years_of_experience} years`],
                ['Travel radius', `${d.travel_radius_km} km`],
                ['Outstation', d.can_travel_outstation ? 'Yes' : 'No'],
                ['Brings samagri', d.has_samagri_capability ? 'Yes' : 'No'],
                ['Service point', d.service_latitude ? `${d.service_latitude}, ${d.service_longitude}` : 'not set'],
                ['Listed in discovery', d.is_listed ? 'Yes' : 'No'],
                ['Activated', d.activated_at ? formatDateTime(d.activated_at) : 'never'],
              ]}
            />
            {d.bio && <p style={{ marginTop: 'var(--sp-md)', color: 'var(--c-ink2)' }}>{d.bio}</p>}
            <div style={{ marginTop: 'var(--sp-md)' }}>
              <ScopeAction scope={scope} need="supply_kyc" size="sm" onClick={() => setListingConfirm(true)}>
                {d.is_listed ? 'Hide from discovery' : 'List in discovery'}
              </ScopeAction>{' '}
              {revealedPhone?.id !== open.id && (
                <ScopeAction scope={scope} need="supply_kyc" size="sm" onClick={() => setPhoneConfirm(true)}>
                  Reveal phone
                </ScopeAction>
              )}
            </div>
          </Section>
        )}
        {actionDialog}
        {listingDialog}
        {phoneDialog}
        {unconfirmDialog}
      </DetailPage>
    );
  }

  return (
    <>
      <FilterBar
        q={q}
        onQ={setQ}
        placeholder="Name, PDT code or phone last 4"
        tabs={TABS.map((t) => ({ key: t.key, label: t.label }))}
        tab={tab}
        onTab={setTab}
        extra={
          <>
            <Select
              ariaLabel="City"
              placeholder="Any city"
              options={(cities.data?.items ?? []).map((c) => c.name)}
              value={city}
              onChange={setCity}
            />
            <Select
              ariaLabel="Listing"
              placeholder="Listed or not"
              options={Object.keys(LISTING)}
              value={listedFilter}
              onChange={setListedFilter}
            />
            <Select
              ariaLabel="Badge"
              placeholder="Any badge"
              options={[...BADGE_BY_LABEL.keys()]}
              value={badge}
              onChange={setBadge}
            />
          </>
        }
      />

      {roster.isPending && <LoadingState message="Reading the roster…" />}
      {roster.isError && (
        <ErrorState title="Could not read the pandit roster" cause={errorMessage(roster.error)} />
      )}
      {roster.isSuccess && rows.length === 0 && (
        <EmptyState
          icon="users"
          title="No pandit matches"
          body="Nothing is wrong — the filter simply has no rows."
        />
      )}
      {rows.length > 0 && (
        <DataTable
          columns={[
            { key: 'display_name', label: 'Name' },
            { key: 'public_code', label: 'Code' },
            { key: 'status', label: 'Status' },
            { key: 'is_listed', label: 'Listed' },
          ]}
          rows={rows.map((p) => ({
            id: p.id,
            display_name: p.display_name,
            public_code: p.public_code,
            status: <StatusChip tone={statusTone(p.status)} label={statusLabel(p.status)} />,
            is_listed: p.is_listed ? 'Yes' : 'No',
          }))}
          focusedIndex={focus}
          onFocusRow={setFocus}
          onRowClick={(r) => setOpen(rows.find((p) => p.id === r.id)!)}
          footer={`${count(roster.data!.total, 'pandit')} · ${QUEUE_HINT}`}
        />
      )}
    </>
  );
}

const pct = (v: number) => `${Math.round(v * 100)}%`;

/**
 * The Quality tab. A pandit with no `score` row has not been measured — the matcher treats them as
 * neutral — so the tiles say "not yet measured" rather than showing 0%, which would read as worst.
 */
function QualityPanel({
  panditId,
  query,
  onSuspend,
  scope,
}: {
  panditId: number;
  query: ReturnType<typeof usePanditQuality>;
  onSuspend: () => void;
  scope: ReturnType<typeof useConsole>['scope'];
}) {
  if (query.isLoading) return <LoadingState />;
  if (query.isError) return <ErrorState title="Could not read quality" cause={errorMessage(query.error)} />;
  const q = query.data!;
  const s = q.score;
  const latest = q.latest;
  const over = s !== null && s.cancellation_rate > q.cancellation_review_rate;
  const none = 'not yet measured';
  return (
    <>
      <Section title="Quality">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 'var(--sp-md)' }}>
          <StatTile label="Acceptance" value={s ? pct(s.acceptance_rate) : '—'} note={s ? undefined : none} />
          <StatTile label="Completion" value={s ? pct(s.completion_rate) : '—'} note={s ? undefined : none} />
          <StatTile
            label="Cancellation"
            value={s ? pct(s.cancellation_rate) : '—'}
            note={s ? (over ? `above the ${pct(q.cancellation_review_rate)} review line` : undefined) : none}
            tone={over ? 'error' : 'default'}
          />
          <StatTile label="On time" value={s ? pct(s.on_time_rate) : '—'} note={s ? undefined : none} />
          <StatTile
            label="Rating"
            value={s && s.rating_count > 0 ? s.rating_avg.toFixed(2) : '—'}
            note={s && s.rating_count > 0 ? `${s.rating_count} reviews` : 'no reviews yet'}
          />
        </div>
        {s && (
          <KV
            rows={[
              ['Completed jobs', String(s.completed_booking_count)],
              ['Bookings, last 7 days', String(s.bookings_last_7d)],
              ['Bookings, last 30 days', String(s.bookings_last_30d)],
              ['Last computed', formatDateTime(s.last_computed_at)],
            ]}
          />
        )}
        {q.trend.length > 1 && (
          <div>
            <Kicker>Completion trend, {q.trend.length} days</Kicker>
            <Sparkline points={q.trend.map((t) => t.completion_rate)} baseline="range" />
          </div>
        )}
        {latest === null && <Note>No snapshot yet — the nightly job has not scored this pandit.</Note>}
        <KV
          rows={[
            ['Gave a booking up', `${q.cancelled_count} (all time)`],
            ['Did not turn up', `${q.no_show_count} (all time)`],
          ]}
        />
      </Section>

      <Section title="Recent reviews" note="Published only, newest first — moderation is on the Reviews screen">
        {q.recent_reviews.length === 0 ? (
          <EmptyState title="No published reviews yet" />
        ) : (
          q.recent_reviews.map((r) => (
            <div key={r.id} style={{ padding: 'var(--sp-xs) 0' }}>
              <strong>★ {r.overall_rating} / 5</strong>
              {r.title && ` · ${r.title}`}
              {r.published_at && <span style={{ color: 'var(--c-ink2)' }}> · {r.published_at.slice(0, 10)}</span>}
              {r.body && <p style={{ margin: 'var(--sp-xs) 0 0', color: 'var(--c-ink2)' }}>{r.body}</p>}
            </div>
          ))
        )}
      </Section>

      <Section title="Open flags">
        {q.open_flags.length === 0 ? (
          <EmptyState title="No open flags" />
        ) : (
          q.open_flags.map((f) => (
            <div key={f.id}>
              <Chip label={f.severity} /> {f.flag_type.replace(/_/g, ' ')} — {f.detail ?? f.source}
            </div>
          ))
        )}
      </Section>

      <QualityActions panditId={panditId} actions={q.actions} scope={scope} onSuspend={onSuspend} />
    </>
  );
}

/** The four formal consequences, mildest first — the server's order (docs/03-data/20 §7). */
const ACTION_TYPES = ['warning', 'training_required', 'temporary_suspension', 'delisting'] as const;

/**
 * Exported for its own test. A suspension ends at the start of the IST day the operator names,
 * so "until 2026-11-01" means they are back on the 1st, not on the 2nd.
 */
export function suspendedUntil(day: string): string | null {
  return /^\d{4}-\d{2}-\d{2}$/.test(day.trim()) ? `${day.trim()}T00:00:00+05:30` : null;
}

/** Exported for its own test: the body names categories and an end only where the type takes them. */
export function issueBody(
  actionType: (typeof ACTION_TYPES)[number],
  reason: string,
  until: string,
  categoryIds: readonly number[],
): QualityActionIssue {
  return {
    action_type: actionType,
    reason,
    effective_until: actionType === 'temporary_suspension' ? suspendedUntil(until) : null,
    category_ids: actionType === 'training_required' ? [...categoryIds].sort((a, b) => a - b) : [],
  };
}

function QualityActions({
  panditId,
  actions,
  scope,
  onSuspend,
}: {
  panditId: number;
  actions: PanditQuality['actions'];
  scope: ReturnType<typeof useConsole>['scope'];
  onSuspend: () => void;
}) {
  const { toast } = useConsole();
  const [issuing, setIssuing] = useState(false);
  const [actionType, setActionType] = useState<(typeof ACTION_TYPES)[number]>('warning');
  const [until, setUntil] = useState('');
  const [picked, setPicked] = useState<number[]>([]);
  const [revoking, setRevoking] = useState<number | null>(null);
  const categories = useCategories();
  const issue = useIssueQualityAction();
  const revoke = useRevokeQualityAction();
  const fail = (e: unknown) => toast({ tone: 'error', message: errorMessage(e) });

  return (
    <Section title="Quality actions">
      {actions.length === 0 ? (
        <EmptyState title="No quality actions" />
      ) : (
        actions.map((a) => (
          <div key={a.id} style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>
            <span>
              <Chip label={a.action_type.replace(/_/g, ' ')} /> {a.status} — {a.reason}
              {a.revoke_reason ? ` (revoked: ${a.revoke_reason})` : ''}
            </span>
            {/* A delisting is permanent; the server refuses its revoke, so no button offers one. */}
            {a.status === 'active' && a.action_type !== 'delisting' && (
              <ScopeAction scope={scope} need="supply_kyc" size="sm" variant="secondary" onClick={() => setRevoking(a.id)}>
                {`Revoke ${a.action_type.replace(/_/g, ' ')}`}
              </ScopeAction>
            )}
          </div>
        ))
      )}
      <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
        <ScopeAction scope={scope} need="supply_kyc" size="sm" onClick={() => setIssuing(true)}>
          Issue an action
        </ScopeAction>
        <ScopeAction scope={scope} need="supply_kyc" size="sm" variant="secondary" onClick={onSuspend}>
          Suspend
        </ScopeAction>
      </div>

      {issuing && (
        <ConfirmAction
          open
          title="Issue a quality action?"
          phrase="ISSUE"
          tone="error"
          confirmLabel="Issue"
          consequences={[
            'The pandit is told in the app, with your reason.',
            'A suspension or a training requirement changes what dispatch offers them from now on.',
            'A delisting is permanent and cannot be revoked.',
          ]}
          reason="Reason (the pandit reads it)"
          onCancel={() => setIssuing(false)}
          onConfirm={(why) =>
            issue.mutate(
              { id: panditId, body: issueBody(actionType, why, until, picked) },
              {
                onSuccess: () => {
                  setIssuing(false);
                  setPicked([]);
                  toast({ tone: 'success', message: `${actionType.replace(/_/g, ' ')} issued` });
                },
                onError: (e) => {
                  setIssuing(false);
                  fail(e);
                },
              },
            )
          }
        >
          <Select
            label="Action"
            value={actionType}
            options={[...ACTION_TYPES]}
            onChange={(next) => setActionType(next as (typeof ACTION_TYPES)[number])}
          />
          {actionType === 'temporary_suspension' && (
            <Input label="Suspended until (IST date)" value={until} onChange={setUntil} placeholder="2026-11-01" />
          )}
          {actionType === 'training_required' && (
            <div style={{ display: 'grid', gap: 'var(--sp-xs)' }}>
              <Note>Categories — none picked holds them back from every puja</Note>
              {categories.isError && (
                <ErrorState title="Could not read the categories" cause={errorMessage(categories.error)} />
              )}
              {(categories.data ?? []).map((c) => (
                <label key={c.id} style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center' }}>
                  <Toggle
                    label={c.name_en}
                    on={picked.includes(c.id)}
                    onChange={(on) =>
                      setPicked((now) => (on ? [...now, c.id] : now.filter((id) => id !== c.id)))
                    }
                  />
                  {c.name_en}
                </label>
              ))}
            </div>
          )}
        </ConfirmAction>
      )}

      {revoking !== null && (
        <ConfirmAction
          open
          title="Revoke this quality action?"
          phrase="REVOKE"
          confirmLabel="Revoke"
          consequences={['It stops applying at once, and the pandit is told.', 'The action stays in the history.']}
          reason="Why it no longer applies"
          onCancel={() => setRevoking(null)}
          onConfirm={(why) =>
            revoke.mutate(
              { actionId: revoking, reason: why },
              {
                onSuccess: () => {
                  setRevoking(null);
                  toast({ tone: 'success', message: 'Revoked' });
                },
                onError: (e) => {
                  setRevoking(null);
                  fail(e);
                },
              },
            )
          }
        />
      )}
    </Section>
  );
}
